import {
  call,
  put,
  takeEvery,
  takeLatest,
  all,
  select,
  fork,
} from 'redux-saga/effects';
import { camelCase } from 'lodash';

import { actions as errorActions } from 'error';
import {
  createOffRampBill,
  fetchOffRamps,
  fetchOffRampPage,
} from '../../app/services/api';
import { getOffRampsScope } from '../reducers';
import actions from '../actions';

const {
  createOffRampBillRequest,
  createOffRampBillSuccess,
  createOffRampBillFailure,
  offRampsRequest,
  offRampsSuccess,
  offRampsFailure,
  offRampsTableNextPage,
  offRampsTablePreviousPage,
  resetOffRampsTable,
  setOffRampsTableFilter,
  setOffRampsTablePage,
  setOffRampsTablePageSize,
  setOffRampsTableSort,
  singleOffRampRequest,
  singleOffRampSuccess,
  singleOffRampFailure,
} = actions;

const sortOffRampFields = {
  createdAt: 'created_at',
  detailsReference: 'reference',
  tradedAmount: 'traded_amount',
  convertedAmount: 'converted_amount',
  status: 'status',
};

const translateSortField = (field) => {
  const fieldParsed = camelCase(field);
  return sortOffRampFields[fieldParsed] || 'created_at';
};

const translateStatus = (statusValue) =>
  statusValue === '.*' ? 'all' : statusValue;

function* handleCreateOffRampBill(action) {
  try {
    const { payload: fiatAmount } = action;
    const response = yield call(createOffRampBill, fiatAmount);

    if (response.error) {
      yield put(createOffRampBillFailure(response.error));
    } else {
      yield put(createOffRampBillSuccess(response.response));
    }
  } catch (error) {
    yield put(createOffRampBillFailure(error));
  }
}

function* fetchSingleOffRamp({ payload }) {
  const { id } = payload;
  const { response, error } = yield call(fetchOffRampPage, id);

  if (response) {
    yield put(singleOffRampSuccess(response));
  } else {
    yield all([
      put(singleOffRampFailure(error)),
      put(errorActions.apiError(error)),
    ]);
  }
}

function* offRampsScopeSelector() {
  return yield select(getOffRampsScope);
}

function* fetchOffRampsFromScope() {
  const {
    dateRange,
    page,
    pageSize,
    search,
    sort: { id: sortId, sortAscending },
    status,
  } = yield offRampsScopeSelector();

  const { response, error } = yield call(fetchOffRamps, {
    dateRange,
    page,
    pageSize,
    search,
    sort: { id: translateSortField(sortId), sortAscending },
    status: translateStatus(status),
  });

  if (response) {
    yield put(offRampsSuccess(response));
  } else {
    yield all([put(offRampsFailure(error)), put(errorActions.apiError(error))]);
  }
}

function* handleFetchOffRamps(action) {
  const { payload } = action;

  if (payload) {
    const { response, error } = yield call(fetchOffRamps, payload);

    if (response) {
      yield put(offRampsSuccess(response));
    } else {
      yield all([
        put(offRampsFailure(error)),
        put(errorActions.apiError(error)),
      ]);
    }
  } else {
    yield call(fetchOffRampsFromScope);
  }
}

function* watchCreateOffRampBill() {
  yield takeEvery(createOffRampBillRequest, handleCreateOffRampBill);
}

function* watchSingleOffRamp() {
  yield takeEvery(singleOffRampRequest, fetchSingleOffRamp);
}

const actionsToTriggerOffRampsFetch = [
  offRampsTableNextPage,
  offRampsTablePreviousPage,
  resetOffRampsTable,
  setOffRampsTableFilter,
  setOffRampsTablePage,
  setOffRampsTablePageSize,
  setOffRampsTableSort,
];

function* watchFetchOffRamps() {
  yield takeLatest(offRampsRequest, handleFetchOffRamps);
}

function* triggerOffRampsRequest() {
  yield put(offRampsRequest());
}

function* watchOffRampsScope() {
  yield takeEvery(actionsToTriggerOffRampsFetch, triggerOffRampsRequest);
}

export default function* offRampRootSaga() {
  yield fork(watchOffRampsScope);
  yield fork(watchFetchOffRamps);
  yield fork(watchCreateOffRampBill);
  yield fork(watchSingleOffRamp);
}
